import { ResizableBox } from 'react-resizable';
import styled from '@emotion/styled';
import css from '@emotion/css';
import './editor.css';
import { EditorContent } from '@tiptap/react';
import { monoFontFamily, truncateCss } from 'app/typography';
import { codeHighlightTokensCss } from 'app/codeHighlight';
import ColumnResizeIcon from 'components/Kizen/Icon/custom/ColumnResizeIcon';
import type { EditorWrapperProps, FloatingEditorWrapperProps } from './types';

export const HorizontalRuleStyle = css`
  hr {
    box-sizing: border-box;
    border-width: 0;
    border-style: solid;
    border-color: inherit;
    margin: 0;
    height: 0;
    color: var(--color-font-placeholder);
    border-top-width: 1px;
  }
`;

const proseMirrorStyle = ({
  defaultFontFamily = 'Arial',
  defaultFontSize = '14px',
} = {}) => css`
  .ProseMirror {
    padding: 10px;
    outline: none;
    word-break: break-word;

    /* Reccomended layout CSS */
    > * + * {
      margin-top: 0;
    }

    ${HorizontalRuleStyle}

    /* Placeholder (at the top) */
    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: var(--color-background-medium);
      pointer-events: none;
      height: 0;
    }

    p * {
      line-height: inherit;
    }

    a[data-clickable] {
      cursor: pointer;
    }

    /* Set default font-family for now */
    font-family: ${defaultFontFamily};
    font-size: ${defaultFontSize};

    /*
    We need to make sure we inherit the font info, otherwise a
    nested <u> or <strong> will revert back the global settings we have
    */
    * {
      font-family: inherit;
      font-size: inherit;
      line-height: 1.5em;
    }

    &&& .mention {
      background: var(--color-background-white);
      position: relative;
      max-width: 250px;
      height: 24px;
      padding-left: 0px;
      padding-right: 0px;
      :focus {
        outline: none;
      }
      bottom: -2px;
      ${truncateCss}
    }
  }
`;

export const StyledResizableBox = styled(ResizableBox)<{ resizing?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  ${({ resizing }) =>
    resizing &&
    css`
      & > div {
        pointer-events: none;
      }
    `}
`;

export const ResizeIcon = styled(ColumnResizeIcon)`
  position: absolute;
  left: 50%;
  bottom: -16px;
  transform: rotate(90deg);
  cursor: ns-resize;

  &&,
  && svg {
    width: 3px;
    height: 30px;
  }
`;

export const BottomBarWrapper = styled.div`
  border-top: 1px solid var(--color-border-medium);
`;

export const StyledEditorContent = styled(EditorContent)<{
  disabled?: boolean;
  blurContent?: boolean;
  enableResize?: boolean;
  initialHeight?: number;
}>`
  flex: 1;
  height: 100%;
  overflow-y: auto;

  & > .tiptap {
    height: 100%;
  }

  ${({ disabled = false }) =>
    disabled &&
    css`
      background-color: var(--color-background-light);
    `}

  ${({ blurContent }) =>
    blurContent &&
    css`
      filter: blur(2px);
    `}

  ${({ enableResize, initialHeight = 200 }) =>
    !enableResize &&
    css`
      height: ${initialHeight}px;
    `}
`;

const codeBlockStyle = ({
  codeBlockColors,
}: Pick<EditorWrapperProps, 'codeBlockColors'>) => css`
  .ProseMirror {
    background: var(--color-code-highlight-surface);
    & > pre > code {
      font-family: ${monoFontFamily};
      font-size: 14px;
      color: var(--color-code-highlight-text);
    }
  }
  ${codeHighlightTokensCss}
  /* Custom colors */
  ${Object.entries(codeBlockColors || {}).reduce((acc, [key, value]) => {
    return (
      acc +
      `
        .hljs-${key} {
          color: ${value};
        }
      `
    );
  }, '')}
`;

export const EditorWrapper = styled.div<EditorWrapperProps>`
  ${proseMirrorStyle}
  ${codeBlockStyle}
  border: 1px solid var(--color-border-medium);
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  background: var(--color-background-white);

  ${({ hasLoadingState }) =>
    hasLoadingState &&
    css`
      position: relative;
      container-type: inline-size;
    `}

  .ProseMirror {
    height: 100%;

    ${({ fitToContent }) =>
      !fitToContent &&
      css`
        overflow: auto;
      `}

    ${({ isLoading }) =>
      isLoading &&
      css`
        min-height: 120px;
      `}
  }
`;

export const FloatingEditorWrapper = styled.div<FloatingEditorWrapperProps>`
  ${proseMirrorStyle}
  ${codeBlockStyle}

  ${({ hasLoadingState }) =>
    hasLoadingState &&
    css`
      position: relative;
      container-type: inline-size;
    `}

  .ProseMirror {
    background: transparent;
    border-radius: 0;
    ${({ showBorder }) =>
      showBorder
        ? css`
            border: 1px solid var(--color-viz-cat-2-primary);
          `
        : css`
            border: none;
          `}

    ${({ isLoading }) =>
      isLoading &&
      css`
        min-height: 120px;
      `}
  }
`;

export const LoadingContainer = styled.div`
  position: absolute;
  top: 20px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;
